import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { SegmentService } from '../../../../core/services/segment.service';
import { UserService } from '../../../../core/services/user.service';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { CheckboxComponent } from '../../../../shared/ui/forms/checkbox/checkbox.component';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';
import { Load } from '../../../../core/models/load.model';
import { LoadSegment } from '../../../../core/models/load-segment.model';
import { Stop } from '../../../../core/models/stop.model';
import { isRelayOnlyStopType } from '../../../../core/utils/itinerary-reconciler';

@Component({
  selector: 'app-merge-leg-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    CheckboxComponent,
    TextComponent,
    LucideIconComponent,
  ],
  template: `
    <div class="flex flex-col">
      <div class="flex flex-col gap-4 p-5">
        <div class="p-3.5 rounded-xl bg-surface border border-border flex items-start gap-3">
          <div class="w-8 h-8 rounded-lg bg-surface-high border border-border flex items-center justify-center shrink-0">
            <lucide-icon name="Merge" size="16" class="text-primary"></lucide-icon>
          </div>
          <div class="flex flex-col gap-1">
            <app-text size="sm" weight="semibold" class="text-foreground">
              Merge Leg {{ targetSegment?.segmentNumber }} into Leg {{ survivingLeg()?.segmentNumber }}
            </app-text>
            <app-text size="xs" color="muted">
              {{ survivingDriverName() }} will cover the combined stops ({{ mergedRangeText() }}).
              Any mobile load view for removed leg drivers will be cleaned up automatically.
            </app-text>
          </div>
        </div>

        @if (relayBoundaryStop(); as rStop) {
          <div class="p-3.5 rounded-xl bg-surface-high border border-border flex flex-col gap-2">
            <app-checkbox
              [label]="'Also remove Relay Stop #' + rStop.stopNumber + ' (' + (rStop.companyName || rStop.address?.city || 'Handoff Point') + ') from the route'"
              [ngModel]="removeRelayStop()"
              (ngModelChange)="removeRelayStop.set($event)"
              [ngModelOptions]="{ standalone: true }"
            ></app-checkbox>
            <app-text size="xs" color="muted" class="pl-6">
              Uncheck if the driver still needs to stop at this yard/location without swapping drivers.
            </app-text>
          </div>
        }
      </div>

      <div class="flex items-center justify-end gap-3 p-4 border-t border-border bg-surface-low rounded-b-xl">
        <app-button variant="ghost" type="button" (click)="cancel()" [disabled]="isSaving()">
          Cancel
        </app-button>
        <app-button
          variant="danger"
          type="button"
          icon="Undo2"
          [loading]="isSaving()"
          (click)="confirmMerge()"
        >
          {{ removeRelayStop() && relayBoundaryStop() ? 'Undo Split & Remove Relay Stop' : 'Undo Split & Merge Leg' }}
        </app-button>
      </div>
    </div>
  `,
})
export class MergeLegModalComponent implements OnInit {
  readonly hasCustomActions = true;

  private readonly modalService = inject(ModalService);
  private readonly segmentService = inject(SegmentService);
  private readonly userService = inject(UserService);
  private readonly toastService = inject(ToastService);

  load!: Load;
  segments: LoadSegment[] = [];
  targetSegment!: LoadSegment;

  readonly isSaving = signal(false);
  readonly removeRelayStop = signal(true);

  readonly survivingLeg = computed<LoadSegment | null>(() => {
    const ordered = [...this.segments].sort((a, b) => a.segmentNumber - b.segmentNumber);
    const idx = ordered.findIndex((s) => s.id === this.targetSegment?.id);
    if (idx > 0) return ordered[idx - 1];
    if (idx === 0 && ordered.length > 1) return ordered[1];
    return null;
  });

  readonly survivingDriverName = computed(() => {
    const leg = this.survivingLeg();
    if (!leg) return 'Primary driver';
    return leg.driverId
      ? this.userService.getDriverDisplayName(leg.driverId, leg.driverName)
      : 'Unassigned driver';
  });

  readonly relayBoundaryStop = computed<Stop | null>(() => {
    const stops = this.load?.stops || [];
    const ordered = [...this.segments].sort((a, b) => a.segmentNumber - b.segmentNumber);
    const idx = ordered.findIndex((s) => s.id === this.targetSegment?.id);
    const boundaryNum =
      idx > 0 ? this.targetSegment?.fromStopNumber : this.targetSegment?.toStopNumber;
    if (!boundaryNum) return null;
    const stop = stops.find(
      (s) =>
        s.stopNumber === boundaryNum &&
        (s.createdForRelay || isRelayOnlyStopType(s.stopType)) &&
        s.status !== 'DEPARTED'
    );
    return stop || null;
  });

  readonly mergedRangeText = computed(() => {
    const surv = this.survivingLeg();
    if (!surv || !this.targetSegment) return '';
    const minStop = Math.min(surv.fromStopNumber, this.targetSegment.fromStopNumber);
    const maxStop = Math.max(surv.toStopNumber, this.targetSegment.toStopNumber);
    const adjustedMax =
      this.removeRelayStop() && this.relayBoundaryStop() ? Math.max(minStop, maxStop - 1) : maxStop;
    return `Stops ${minStop}–${adjustedMax}`;
  });

  ngOnInit(): void {
    const data = this.modalService.state()?.data || {};
    this.load = data.load;
    this.segments = data.segments || [];
    this.targetSegment = data.segment;
    this.removeRelayStop.set(Boolean(this.relayBoundaryStop()));
  }

  async confirmMerge(): Promise<void> {
    if (!this.load || !this.targetSegment) return;
    this.isSaving.set(true);
    try {
      await this.segmentService.mergeLegWithPrevious(
        this.load,
        this.targetSegment.id,
        {
          removeRelayStop: Boolean(this.relayBoundaryStop() && this.removeRelayStop()),
        },
        this.segments
      );
      this.toastService.show(
        'success',
        this.relayBoundaryStop() && this.removeRelayStop()
          ? 'Route split undone and relay stop removed.'
          : 'Legs merged cleanly.',
        { title: 'Route Reconciled' }
      );
      this.modalService.state()?.onSuccess?.();
      this.modalService.close();
    } catch (err: any) {
      console.error('Failed to merge leg:', err);
      this.toastService.show('error', err?.message || 'Failed to merge leg.', {
        title: 'Merge Failed',
      });
    } finally {
      this.isSaving.set(false);
    }
  }

  cancel(): void {
    this.modalService.close();
  }
}
